/* eslint-disable no-underscore-dangle */
import {
  computed,
  Ref,
  ref,
  useContext,
  useRouter,
} from '@nuxtjs/composition-api';
import {
  Cart,
  CartItemInterface,
  ConfigurableCartItem,
  Product,
  QuoteItemUpdateInput,
  RemoveItemFromCartInput,
  UpdateQuoteItemsInput,
  useCart,
  useCartView,
  useUiNotification,
} from '~/composables';
import useOmnibusPrice from '~/modules/catalog/product/composables/useOmnibusPrice';
import { Logger } from '~/helpers/logger';
import cartGetters from '~/modules/checkout/getters/cartGetters';
import {
  UseRequestQuoteErrors,
  UseRequestQuoteInterface,
} from './useRequestQuote';
import { useRequestQuoteAPI } from '../useRequestQuoteAPI';
import requestQuoteCartGetters from '../../getters/requestQuoteCartGetters';
import { RequestQuoteFormFields } from '../../types';
import { useRequestQuoteStore } from '../../store/requestQuoteStore';
import { SearchCustomerRequestQuotesParams } from '../useRequestQuoteAPI/useRequestQuoteAPI';

// ! We have translated strigs containing that number too.
// ! Please update strings when changing this variable.
const MIN_QUOTE_CART_PRICE_PLN = 10_000;

export function useRequestQuote(): UseRequestQuoteInterface {
  const loading = ref(false);
  const error: Ref<UseRequestQuoteErrors> = ref({
    handleUpdateQuoteItemsInCart: null,
    handleSubmitQuote: null,
    searchCustomerRequestQuotes: null,
  });

  const isRemoveModalVisible = ref(false);
  const itemToRemove = ref<CartItemInterface>();
  const itemsToUpdate = ref<QuoteItemUpdateInput[]>([]);

  const {
    createEmptyQuoteCart,
    moveInQuote,
    customerQuoteCart,
    clearQuote,
    removeItemFromQuoteCart,
    addProductsToQuoteCart,
    updateQuoteItemsInCart,
    submitQuote,
    searchCustomerRequestQuotes,
    moveInCart,
  } = useRequestQuoteAPI();
  const { cart, load: loadCart } = useCart();
  const { removeAllItems: removeAllItemsFromCart } = useCartView();
  const router = useRouter();
  const { localePath, i18n, app } = useContext();
  const { omnibusPrices } = useOmnibusPrice();
  const { send: sendNotification, notifications } = useUiNotification();
  const requestQuoteStore = useRequestQuoteStore();

  // current customer quote cart
  const currentCustomerQuoteCart = computed(() => requestQuoteStore.currentCustomerQuoteCart);
  // form values for the RequestQuoteForm
  const requestQuoteFormValues = computed(() => requestQuoteStore.requestQuoteFormValues);
  // flag if user wants to add remarks to the quote
  const withRemarks = computed(() => requestQuoteStore.withRemarks);
  // list of customer request quotes
  const customerRequestQuotes = computed(() => requestQuoteStore.customerRequestQuotes);

  // list of products in the quote cart
  const products = computed(() => requestQuoteCartGetters
    .getItems(currentCustomerQuoteCart.value)
    .filter(Boolean)
    .map((item) => {
      const { sku: currentProductSku } = cartGetters.isProductConfigurable(item)
        ? cartGetters.getConfiguredVariant(
          item as ConfigurableCartItem,
        )
        : item.product;
      return {
        ...item,
        product: {
          ...item.product,
          ...[(item as ConfigurableCartItem).configured_variant ?? {}],
          original_sku: item.product.sku,
          omnibus_price: omnibusPrices?.value
            ? omnibusPrices.value[currentProductSku]
            : '',
        },
      };
    }));
  // total number of items in the quote cart
  const totalItems = computed(() => cartGetters.getTotalItems(currentCustomerQuoteCart.value));
  // total price of the quote cart
  const totals = computed(() => cartGetters.getTotals(currentCustomerQuoteCart.value));
  // discount amount of the quote cart
  const discount = computed(
    () => -cartGetters.getDiscountAmount(currentCustomerQuoteCart.value),
  );

  const showRemoveItemModal = ({ product }: { product: CartItemInterface }) => {
    if (notifications.value.length > 0) {
      notifications.value[0].dismiss();
    }

    isRemoveModalVisible.value = true;
    itemToRemove.value = product;
  };

  const handleRemoveItemFromQuoteCart = async ({
    cart_id,
    cart_item_uid,
  }: RemoveItemFromCartInput) => {
    const updatedQuoteCart: any = await removeItemFromQuoteCart({
      cart_id,
      cart_item_uid,
    });
    requestQuoteStore.setCustomerQuoteCart(updatedQuoteCart.cart as Cart);
  };

  // remove item from the quote cart and send notification
  const removeItemAndSendNotification = async (product: CartItemInterface) => {
    Logger.debug('useRequestQuote.removeItemAndSendNotification', {
      productUid: product.uid,
    });

    const currentQuoteId = await (
      app.$vsf.$magento.config.state as any
    ).getQuoteId();

    await handleRemoveItemFromQuoteCart({
      cart_id: currentQuoteId,
      cart_item_uid: product.uid,
    });
    isRemoveModalVisible.value = false;

    sendNotification({
      id: Symbol('product_removed'),
      message: i18n.t('x was successfully removed from the quote', {
        x: cartGetters.getItemName(product),
      }) as string,
      type: 'success',
      icon: 'check',
      persist: false,
      title: 'Product removed',
    });
  };

  const handleClearQuoteCart = async () => {
    const currentQuoteId = await (
      app.$vsf.$magento.config.state as any
    ).getQuoteId();
    if (!currentQuoteId) return;
    const cleanQuoteCart = await clearQuote(currentQuoteId as string);
    requestQuoteStore.setCustomerQuoteCart(cleanQuoteCart as Cart);
  };

  // move items from the cart to the quote cart - this action cleans the cart
  const handleRequestQuoteFromCart = async () => {
    Logger.debug('useRequestQuote.handleRequestQuoteFromCart', {
      cartId: cart.value.id,
    });

    try {
      await handleClearQuoteCart();

      let currentQuoteId = await (
        app.$vsf.$magento.config.state as any
      ).getQuoteId();

      if (!currentQuoteId) {
        const createdQuoteCartId = await createEmptyQuoteCart({});
        if (!createdQuoteCartId) {
          throw new Error('Unable to create a new quote cart');
        }
        currentQuoteId = createdQuoteCartId;
        await (app.$vsf.$magento.config.state as any).setQuoteId(
          createdQuoteCartId,
        );
      }
      const moveInQuoteRes = await moveInQuote({
        cartId: cart.value.id,
        quoteId: currentQuoteId,
      });
      if (!moveInQuoteRes || !moveInQuoteRes.result) {
        throw new Error('Unable to move items to the quote cart');
      }

      const quoteCart = await customerQuoteCart();
      requestQuoteStore.setCustomerQuoteCart(quoteCart as Cart);

      await loadCart();

      router.push(localePath('/request-quote'));

      sendNotification({
        id: Symbol('quote-request-success'),
        message: i18n.t('Quote cart has been created') as string,
        type: 'success',
        icon: 'check',
        persist: false,
        title: 'Quote',
      });
    } catch (err) {
      Logger.error('useRequestQuote.handleRequestQuoteFromCart', err);
      sendNotification({
        id: Symbol('quote-request-error'),
        message: i18n.t(err.message as string) as string,
        type: 'warning',
        icon: 'check',
        persist: false,
        title: 'Quote',
      });
    }
  };

  // add products to the quote cart
  const handleAddProductsToQuoteCart = async ({
    product,
    quantity = 1,
    bundleSelectedOptions,
    productConfiguration,
    redirect = false,
  }: {
    product: Product;
    quantity?: number;
    bundleSelectedOptions?: Record<string, { uid: string; quantity: number }>;
    productConfiguration?: Record<string, string>;
    redirect?: boolean;
  }) => {
    Logger.debug('useRequestQuote.handleAddProductsToQuoteCart', {
      product,
    });

    const productType = product.__typename;

    try {
      let currentQuoteId = await (
        app.$vsf.$magento.config.state as any
      ).getQuoteId();
      if (!currentQuoteId) {
        currentQuoteId = await createEmptyQuoteCart(cart.value?.id && {
          quote_mask_id: cart.value?.id,
        });
      }

      let newCart;
      switch (productType) {
        case 'SimpleProduct':
        case 'VirtualProduct':
          newCart = await addProductsToQuoteCart({
            quoteId: currentQuoteId,
            quoteItems: [
              {
                sku: product.sku,
                quantity,
              },
            ],
          });
          break;
        case 'BundleProduct':
          if (!bundleSelectedOptions) {
            sendNotification({
              id: Symbol('product-added-error'),
              message: i18n.t('Choose a variant') as string,
              type: 'warning',
              icon: 'check',
              persist: false,
              title: 'Product Added',
            });
            break;
          }
          const bundleProductData = Object.keys(bundleSelectedOptions).map(
            (selectedOptionKey) => {
              const selectedOption = bundleSelectedOptions[selectedOptionKey];
              return {
                uid: selectedOption.uid,
                value: selectedOption.quantity,
              };
            },
          );

          const createEnteredOptions = () => bundleProductData.map((bundleOption) => ({
            ...bundleOption,
            value: bundleOption.value.toString(),
          }));

          newCart = await addProductsToQuoteCart({
            quoteId: currentQuoteId,
            quoteItems: [
              {
                sku: product.sku,
                quantity,
                entered_options: createEnteredOptions(),
              },
            ],
          });
          break;
        case 'ConfigurableProduct':
        case 'GroupedProduct':
          if (!productConfiguration) {
            sendNotification({
              id: Symbol('product-added-error'),
              message: i18n.t('Choose a variant') as string,
              type: 'warning',
              icon: 'check',
              persist: false,
              title: 'Product Added',
            });
            break;
          }
          const selectedOptions = Object.values(productConfiguration as object);
          newCart = await addProductsToQuoteCart({
            quoteId: currentQuoteId,
            quoteItems: [
              {
                sku: product.sku,
                quantity,
                selected_options: selectedOptions,
              },
            ],
          });
          break;
        default:
          throw new Error(
            app.i18n.t('Product Type x not supported in add to cart yet', {
              x: productType,
            }) as string,
          );
      }

      if (!newCart) {
        throw new Error('Unable to add product to the quote');
      } else if (newCart.user_errors.length > 0) {
        throw new Error(newCart.user_errors[0].message as string);
      }

      requestQuoteStore.setCustomerQuoteCart(newCart.cart as Cart);

      sendNotification({
        id: Symbol('product_added'),
        message: i18n.t('The product has been added to the offer') as string,
        type: 'success',
        icon: 'check',
        persist: false,
        title: 'Product Added',
      });

      if (redirect) {
        router.push(localePath('/request-quote'));
      }
    } catch (err) {
      Logger.error('useRequestQuote.handleAddProductsToQuoteCart', err);
      const errorMessage = err.message || 'Unable to add product to the quote';

      sendNotification({
        id: Symbol('product-added-error'),
        message: i18n.t(`${errorMessage}`) as string,
        type: 'warning',
        icon: 'check',
        persist: false,
        title: 'Product Added',
      });
    }
  };

  // fetching current customer quote cart
  const fetchCustomerQuoteCart = async () => {
    const quoteCart = await customerQuoteCart();
    requestQuoteStore.setCustomerQuoteCart(quoteCart as Cart);

    if (currentCustomerQuoteCart.value) {
      const quoteId = currentCustomerQuoteCart.value.id;
      await (app.$vsf.$magento.config.state as any).setQuoteId(quoteId);
    }
  };

  const handleUpdateQuoteItemsInCart = async (input: UpdateQuoteItemsInput) => {
    loading.value = true;
    Logger.debug('useRequestQuote.handleUpdateQuoteItemsInCart', {
      input,
    });

    try {
      const updatedQuoteCart: any = await updateQuoteItemsInCart({
        ...input,
        remarks: requestQuoteFormValues.value.remarks,
      });
      requestQuoteStore.setCustomerQuoteCart(updatedQuoteCart.cart as Cart);

      sendNotification({
        id: Symbol('quote-product-update-success'),
        message: app.i18n.t(
          'Successfully updated product in the quote',
        ) as string,
        type: 'success',
        icon: 'check',
        persist: false,
        title: 'Quote',
      });
      error.value.handleUpdateQuoteItemsInCart = null;
    } catch (err) {
      Logger.error('useRequestQuote.handleUpdateQuoteItemsInCart', err);
      sendNotification({
        id: Symbol('quote-product-update-error'),
        message: app.i18n.t('Unable to update product in the quote') as string,
        type: 'warning',
        icon: 'check',
        persist: false,
        title: 'Quote',
      });
      error.value.handleUpdateQuoteItemsInCart = err;
    } finally {
      loading.value = false;
    }
  };

  // update item in the quote cart
  const handleUpdateItem = async ({
    price,
    defaultPrice,
    quantity,
    cart_item_uid,
  }: {
    price?: number;
    defaultPrice?: number;
    quantity?: number;
    cart_item_uid: string;
  }) => {
    loading.value = true;
    const itemsToUpdateValue = itemsToUpdate.value;
    const itemIndex = itemsToUpdateValue.findIndex(
      (item) => item.cartItemUpdateInput.cart_item_uid === cart_item_uid,
    );

    const currentPrice = price || defaultPrice;

    if (itemIndex !== -1) {
      itemsToUpdateValue[itemIndex] = {
        ...itemsToUpdateValue[itemIndex],
        price: currentPrice,
        note: '',
        cartItemUpdateInput: {
          cart_item_uid,
          ...(quantity && { quantity }),
        },
      };
    } else {
      itemsToUpdateValue.push({
        price: currentPrice,
        note: '',
        cartItemUpdateInput: {
          cart_item_uid,
          ...(quantity && { quantity }),
        },
      });
    }

    itemsToUpdate.value = itemsToUpdateValue;

    const currentQuoteId = await (
      app.$vsf.$magento.config.state as any
    ).getQuoteId();

    await handleUpdateQuoteItemsInCart({
      cart_id: currentQuoteId,
      cart_items: itemsToUpdateValue,
    });

    loading.value = false;
  };

  const prepareProductsToSubmit = (): QuoteItemUpdateInput[] => {
    const preparedProducts = products.value.map((el) => ({
      price: el.prices.row_total_including_tax.value / el.quantity,
      note: '',
      cartItemUpdateInput: {
        cart_item_uid: el.uid,
        quantity: el.quantity,
      },
    }));

    return preparedProducts;
  };

  // submit the quote, remove quoteId from the cookies and redirect to the thank you page
  const handleSubmitQuote = async () => {
    loading.value = true;

    const currentQuoteId = await (
      app.$vsf.$magento.config.state as any
    ).getQuoteId();

    const areRequiredQuoteFieldsFilled = Boolean(
      requestQuoteFormValues.value.email
      && requestQuoteFormValues.value.firstName
      && requestQuoteFormValues.value.lastName,
    );
    if (!areRequiredQuoteFieldsFilled) {
      sendNotification({
        id: Symbol('quote-submit-error'),
        message: app.i18n.t('Fill in the mandatory fields') as string,
        type: 'warning',
        icon: 'check',
        persist: false,
        title: 'Quote',
      });
      loading.value = false;
      return;
    }

    const preparedProducts = prepareProductsToSubmit();
    Logger.debug('useRequestQuote.handleSubmitQuote', {
      cart: cart.value,
      preparedProducts,
    });

    try {
      const submittedQuote = await submitQuote({
        customerInput: {
          email: requestQuoteFormValues.value.email,
          firstname: requestQuoteFormValues.value.firstName,
          lastname: requestQuoteFormValues.value.lastName,
        },
        updateQuoteItemsInput: {
          cart_id: currentQuoteId,
          cart_items: preparedProducts,
          remarks: withRemarks.value
            ? requestQuoteFormValues.value.remarks
            : '',
        },
      });

      if (!submittedQuote) {
        throw new Error('Unable to submit the quote');
      }

      await fetchCustomerQuoteCart();

      sendNotification({
        id: Symbol('quote-submit-success'),
        message: app.i18n.t('Quote was successfully submitted') as string,
        type: 'success',
        icon: 'check',
        persist: false,
        title: 'Quote',
      });

      (app.$vsf.$magento.config.state as any).removeQuoteId();
      router.push(localePath('/request-quote-thank-you'));
    } catch (err) {
      Logger.error('useRequestQuote.handleSubmitQuote', err);
      sendNotification({
        id: Symbol('quote-submit-error'),
        message: app.i18n.t('Unable to submit the quote') as string,
        type: 'warning',
        icon: 'check',
        persist: false,
        title: 'Quote',
      });
      error.value.handleSubmitQuote = err;
    } finally {
      loading.value = false;
    }
  };

  const handleUpdateRequestQuoteFormValues = (
    values: RequestQuoteFormFields,
    remarksFlag: boolean = withRemarks.value,
  ) => {
    requestQuoteStore.setRequestQuoteFormValues(values);
    requestQuoteStore.setWithRemarks(remarksFlag);
  };

  const handleSearchCustomerRequestQuotes = async (params: SearchCustomerRequestQuotesParams) => {
    const currentCustomerRequestQuotes = await searchCustomerRequestQuotes(params);
    if (currentCustomerRequestQuotes) {
      requestQuoteStore.setCustomerRequestQuotes(currentCustomerRequestQuotes);
    }
  };

  const handleMoveInCart = async (quoteUid: string) => {
    await removeAllItemsFromCart();
    const moveInQuoteRes = await moveInCart({
      cartId: cart.value.id,
      quoteUid,
    });
    if (moveInQuoteRes) {
      await loadCart();
      router.push(localePath('/cart'));
    }
  };

  return {
    loading,
    error,
    handleRequestQuoteFromCart,
    currentCustomerQuoteCart,
    handleClearQuoteCart,
    products,
    totalItems,
    totals,
    discount,
    handleRemoveItemFromQuoteCart,
    showRemoveItemModal,
    removeItemAndSendNotification,
    isRemoveModalVisible,
    itemToRemove,
    fetchCustomerQuoteCart,
    itemsToUpdate,
    handleUpdateItem,
    handleSubmitQuote,
    requestQuoteFormValues,
    withRemarks,
    handleUpdateRequestQuoteFormValues,
    handleAddProductsToQuoteCart,
    MIN_QUOTE_CART_PRICE_PLN,
    handleSearchCustomerRequestQuotes,
    customerRequestQuotes,
    handleMoveInCart,
  };
}
